import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContactProfileCreateSchema } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Input, Select } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';
import { TIMEZONES } from '@xsc/shared';

interface PublicCustomerConfig {
  wildix_kite_provider?: 'collab7' | 'x-bees' | 'both' | 'none' | null;
}

/**
 * Render the per-contact KITE URLs as a labeled, copyable list.
 *
 * Replaces the old "Chat (Collab)" / "Schedule (x-bees)" button strip.
 * The URLs themselves are what the user actually needs — they paste them
 * into outbound emails, SMS, signatures, or document templates. Buttons
 * implied "click here to act" semantics that confused operators.
 */
function KiteUrls({ email }: { email: string }): JSX.Element | null {
  const cfgQ = useQuery<PublicCustomerConfig>({
    queryKey: ['me', 'customer-config-public'],
    queryFn: () => api.get<PublicCustomerConfig>('/v1/me/customer-config-public'),
    staleTime: 60_000,
  });
  const provider = cfgQ.data?.wildix_kite_provider ?? 'both';
  if (provider === 'none') return null;
  const showCollab = provider === 'collab7' || provider === 'both';
  const showXbees = provider === 'x-bees' || provider === 'both';
  const rows: Array<{ label: string; href: string }> = [];
  if (showCollab) {
    rows.push({
      label: 'Collaboration 7 chat',
      href: `https://app.wildix.com/kite/${encodeURIComponent(email)}`,
    });
  }
  if (showXbees) {
    rows.push({
      label: 'x-bees scheduling',
      href: `https://app.x-bees.com/kite/${encodeURIComponent(email)}/schedule`,
    });
  }
  if (rows.length === 0) return null;
  return (
    <div className="rounded border border-border bg-surface-alt p-3 text-sm space-y-2 min-w-[18rem]">
      <div className="text-xs font-medium text-ink-muted uppercase tracking-wide">
        KITE links for this contact
      </div>
      {rows.map((r) => (
        <KiteUrlRow key={r.label} label={r.label} href={r.href} />
      ))}
    </div>
  );
}

function KiteUrlRow({ label, href }: { label: string; href: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop — older browsers without clipboard API */
    }
  };
  return (
    <div>
      <div className="text-xs text-ink-muted">{label}</div>
      <div className="flex items-center gap-1">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 truncate font-mono text-xs text-accent hover:underline"
          title={href}
        >
          {href}
        </a>
        <button
          type="button"
          onClick={onCopy}
          className="rounded border border-border bg-surface px-1.5 py-0.5 text-xs hover:bg-surface-alt"
          title="Copy URL"
        >
          {copied ? '✓' : '📋'}
        </button>
      </div>
    </div>
  );
}

interface FormState {
  name: string;
  contact_name: string;
  organization_name: string;
  direct_number: string;
  reply_to_email: string;
  timezone: string;
}

const EMPTY: FormState = {
  name: '',
  contact_name: '',
  organization_name: '',
  direct_number: '',
  reply_to_email: '',
  timezone: '',
};

interface ContactProfile {
  id: string;
  name: string;
  contact_name?: string;
  organization_name?: string;
  direct_number?: string;
  reply_to_email?: string;
  timezone?: string;
}

export function EditContactProfilePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const profileQ = useQuery({
    queryKey: ['contact-profile', id],
    queryFn: () => api.get(`/v1/contact-profiles/${id}`) as Promise<ContactProfile>,
    enabled: !!id,
  });

  useEffect(() => {
    if (!profileQ.data) return;
    const p = profileQ.data;
    setForm({
      name: p.name ?? '',
      contact_name: p.contact_name ?? '',
      organization_name: p.organization_name ?? '',
      direct_number: p.direct_number ?? '',
      reply_to_email: p.reply_to_email ?? '',
      timezone: p.timezone ?? '',
    });
  }, [profileQ.data]);

  const save = useMutation({
    mutationFn: (payload: unknown) => api.put(`/v1/contact-profiles/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-profiles'] });
      qc.invalidateQueries({ queryKey: ['contact-profile', id] });
      navigate('/contact-profiles');
    },
    onError: (err) => setBannerError(extractDetail(err)),
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    // Double-save guard: if the mutation is already in flight, ignore
    // any extra submit (rapid double-click, double-Enter, autofill).
    if (save.isPending) return;
    setBannerError(null);

    const payload: Record<string, unknown> = { name: form.name.trim() };
    if (form.contact_name.trim()) payload['contact_name'] = form.contact_name.trim();
    if (form.organization_name.trim()) payload['organization_name'] = form.organization_name.trim();
    if (form.direct_number.trim()) payload['direct_number'] = form.direct_number.trim();
    if (form.reply_to_email.trim()) payload['reply_to_email'] = form.reply_to_email.trim();
    if (form.timezone.trim()) payload['timezone'] = form.timezone.trim();

    const fieldIssues = fieldErrors(ContactProfileCreateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    save.mutate(payload);
  };

  const update = (key: keyof FormState) => (value: string): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  if (profileQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (profileQ.error) return <FormBanner message={extractDetail(profileQ.error)} />;

  const contactEmail = form.reply_to_email.trim();

  return (
    <div className="max-w-2xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Edit contact profile</h1>
          <p className="text-sm text-ink-muted">
            Caller-ID and per-contact configuration. Existing references on events and campaigns
            are not affected by name changes.
          </p>
        </div>
        {contactEmail && <KiteUrls email={contactEmail} />}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Name" required error={errors?.['name']} hint="Internal label">
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  onChange={(e) => update('name')(e.target.value)}
                  required
                  autoFocus
                />
              )}
            </FormField>

            <FormField
              label="Contact name"
              hint="Display name of the contacting party"
              error={errors?.['contact_name']}
            >
              {(id) => (
                <Input id={id} value={form.contact_name} onChange={(e) => update('contact_name')(e.target.value)} />
              )}
            </FormField>

            <FormField
              label="Organization name"
              hint="Used as Caller ID Name"
              error={errors?.['organization_name']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.organization_name}
                  onChange={(e) => update('organization_name')(e.target.value)}
                />
              )}
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Caller ID number"
                hint="E.164, e.g. +15555550100"
                error={errors?.['direct_number']}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.direct_number}
                    onChange={(e) => update('direct_number')(e.target.value)}
                    placeholder="+15555550100"
                  />
                )}
              </FormField>

              <FormField label="Reply-to email" error={errors?.['reply_to_email']}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    value={form.reply_to_email}
                    onChange={(e) => update('reply_to_email')(e.target.value)}
                  />
                )}
              </FormField>
            </div>

            <FormField
              label="Timezone"
              hint="Display timezone for this contact's local time"
              error={errors?.['timezone']}
            >
              {(id) => (
                <Select
                  id={id}
                  value={form.timezone}
                  onChange={(e) => update('timezone')(e.target.value)}
                >
                  <option value="">— Use customer default —</option>
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/contact-profiles')}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
